import { LegalEntityDto, NaturalPersonDto } from "api/actor"
import { CopropertyDto } from "api/coproperty"
import { Button } from "components/ui/Button"
import { EyeIcon } from "components/icons/Icons"
import { GenericLoading } from "components/ui/Loader"
import PersonOverview, { LegalEntityDetails, isLegalEntity } from "features/person-view/components/PersonOverview"
import { Building, Building2, Hash, Mail, MapPin, Phone } from "lucide-react"
import { FC, ReactNode, useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { contractsAPI, copropertiesApi } from "services/swagger-api"
import { useContractInfosState } from "../ContractInfosContext"

interface PartyCardProps {
  /** Rôle dans le contrat, en surtitre ("Souscripteur", "Représentant"). */
  role: string
  /** Qualité de la partie, en pastille ("Syndicat des copropriétaires", "Syndic"). */
  kind: string
  icon: ReactNode
  title: string
  details: ReactNode
  action?: ReactNode
}

const PartyCard: FC<PartyCardProps> = ({ role, kind, icon, title, details, action }) => (
  <div className="flex flex-col gap-4 rounded-xl border bg-white p-5">
    <div className="flex items-center justify-between gap-3">
      <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">{role}</span>
      <span className="rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-medium text-primary">{kind}</span>
    </div>
    <div className="flex items-start gap-4">
      <span className="flex h-14 w-14 flex-none items-center justify-center rounded-full bg-primary/10 text-primary">
        {icon}
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate text-xl font-bold text-zinc-800">{title}</p>
        <div className="mt-1.5 flex flex-col gap-1 text-sm text-gray-600">{details}</div>
      </div>
      {action}
    </div>
  </div>
)

const Detail: FC<{ icon: ReactNode; children: ReactNode }> = ({ icon, children }) => (
  <span className="flex items-center gap-1.5">
    <span className="h-4 w-4 flex-none text-primary/70">{icon}</span>
    <span className="truncate">{children}</span>
  </span>
)

/**
 * Onglet « Souscripteur » d'un contrat MRI. Le souscripteur est le syndicat des
 * copropriétaires, porté par la copropriété du risque ; la personne morale rattachée au
 * contrat est son syndic, qui le représente — deux parties côte à côte, puis la fiche
 * légale du syndic.
 */
export const ContractMriInsuredOverview: FC = () => {
  const { contractId } = useContractInfosState()
  const navigate = useNavigate()
  const [coproperty, setCoproperty] = useState<CopropertyDto>()
  const [representative, setRepresentative] = useState<LegalEntityDto | NaturalPersonDto>()
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const load = async () => {
      try {
        setIsLoading(true)
        const [copropertyResponse, insuredsResponse] = await Promise.all([
          copropertiesApi.getCopropertyByContractId({ contractId }),
          contractsAPI.getInsureds({ contractId }),
        ])
        setCoproperty(copropertyResponse.coproperty)
        setRepresentative(insuredsResponse.insureds?.[0])
      } catch (error) {
        console.error("Erreur lors de la récupération du souscripteur du contrat MRI :", error)
      } finally {
        setIsLoading(false)
      }
    }
    load()
  }, [contractId])

  if (isLoading) {
    return (
      <div className="relative py-4">
        <GenericLoading isParentSized loading />
      </div>
    )
  }

  const address = coproperty?.address?.adresseFormattee || coproperty?.address?.ligne1 || null
  const firm = isLegalEntity(representative) ? representative : undefined

  return (
    <div className="flex flex-col gap-6">
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {coproperty ? (
          <PartyCard
            role="Souscripteur"
            kind="Syndicat des copropriétaires"
            icon={<Building className="h-7 w-7" />}
            title={coproperty.name || address || "Copropriété"}
            details={
              <>
                {address && <Detail icon={<MapPin className="h-4 w-4" />}>{address}</Detail>}
                {coproperty.registrationNumber && (
                  <Detail icon={<Hash className="h-4 w-4" />}>Immatriculation {coproperty.registrationNumber}</Detail>
                )}
              </>
            }
          />
        ) : (
          <div className="rounded-xl border bg-white p-5 text-sm text-gray-500">Aucune copropriété associée à ce contrat.</div>
        )}

        {firm ? (
          <PartyCard
            role="Représentant"
            kind="Syndic"
            icon={<Building2 className="h-7 w-7" />}
            title={firm.companyName ?? ""}
            details={
              <>
                {firm.registrationNumber && <Detail icon={<Hash className="h-4 w-4" />}>SIRET {firm.registrationNumber}</Detail>}
                {firm.email && <Detail icon={<Mail className="h-4 w-4" />}>{firm.email}</Detail>}
                {firm.phone && <Detail icon={<Phone className="h-4 w-4" />}>{firm.phone}</Detail>}
              </>
            }
            action={
              firm.id && (
                <Button color="slate" round onClick={() => navigate(`/mes-clients/${firm.id}`)}>
                  <EyeIcon />
                </Button>
              )
            }
          />
        ) : representative ? (
          <PersonOverview person={representative} />
        ) : (
          <div className="rounded-xl border bg-white p-5 text-sm text-gray-500">Aucun représentant n'est lié à ce contrat.</div>
        )}
      </div>

      {firm && <LegalEntityDetails entity={firm} />}
    </div>
  )
}
